import * as React from 'react';
import { useEffect, useState } from 'react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Material UI Packages. */
import { Box, Button, InputLabel, Paper } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { operationRangeDaySlot } from 'shared/ui/OperationPickersDay';

/** Material UI Icons. */
import ArchiveIcon from '@mui/icons-material/Archive';
import AddIcon from '@mui/icons-material/Add';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import { customized_style } from 'shared/config/Styles';
import { AutocompleteFieldTradePartner } from 'shared/ui/AutoCompleteForm';
import ThemeButton from 'shared/ui/Button';
import { CircularLoading } from 'shared/ui/CircularLoading';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import { MainDatagrid } from 'shared/ui/Datagrid';
import DownloadBlobAsZip from 'shared/lib/downloadBlobAsZip';
import { PageHeader } from 'shared/ui/HeaderIcon';
import validateFields from 'shared/lib/validateFields';
import useUserRole from 'shared/hooks/useUserRole';

import { getColumns } from './lib/QuoteArchiveDef';
import QuoteDialog from './components/QuoteDialog';
import { isAssistantManagerOrAbove } from 'shared/lib/roles';

export default function QuoteArchive() {
    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Get a current userRole array. */
    const userRole = useUserRole();

    /** State for search inputs that includes 'trade partner' and 'quotation date'. */
    const [searchData, setSearchData] = useState({ tradePartner: null, quotationDate: [null, null] });
    /** State for saving trade partner list from server. */
    const [tradePartnerList, setTradePartnerList] = useState([]);
    /** State for saving quotation list from server. */
    const [quoteRows, setQuoteRows] = useState([]);
    /** State for open and close new quotation file upload dialog. */
    const [newOpen, setNewOpen] = useState(false);
    /** Loading state for initial page load. */
    const [initialLoading, setInitialLoading] = useState(false);
    /** Loading state for user actions (delete, download). */
    const [actionLoading, setActionLoading] = useState(false);
    /** State for triggering first GET API. */
    const [rowUpdateTrigger, setRowUpdateTrigger] = useState(false);
    /** State for saving the data grid's current page and page size info. */
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
    /** State for show the total amount of data grid's data. */
    const [rowCount, setRowCount] = useState(100);
    /** State for validating search input. */
    const [searchValidator, setSearchValidator] = useState({ quotationDate: false });

    /** Named website title. */
    useEffect(() => {
        document.title = 'Quote Archive | Operation System';
    }, []);

    /** Make a GET API request when the page is initially rendered.
     * Re-fetches data whenever `rowUpdateTrigger` is triggereed or the pagination setting is modified.
     */
    useEffect(() => {
        callAPI({
            method: 'get',
            module: 'quote_archive',
            component: null,
            type: JSON.stringify(paginationModel),
            getAccessTokenSilently,
            email: user.email,
            onInit: () => setInitialLoading(false),
            onSuccess: (resp) => {
                setTradePartnerList(resp.data.il_trade_partner);
                setRowCount(resp.data.total);
                setQuoteRows(resp.data.il_quotation);
            },
            onError: () =>
                dispatch(
                    setSnackbar({ open: true, message: 'Something wrong, please try again. ', severity: 'error' })
                ),
            onFinal: () => setInitialLoading(true),
        });
    }, [user.email, rowUpdateTrigger, paginationModel]);

    /** Handler for open new quote dialog, but permission is restricted based on userRole.
     * Assistant Manager and above (Operation-Manager, Administrator) can create.
     */
    const handleOpenDialog = () => {
        if (isAssistantManagerOrAbove(userRole)) {
            setNewOpen(true);
        } else
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'You do not have permission to create a new quote.',
                    severity: 'warning',
                })
            );
    };

    /** Handler for saving trade partner and quotation date input. */
    const handleSearchFormChange = (key, newValue) => {
        setSearchData((prev) => ({
            ...prev,
            [key]: newValue,
        }));
    };

    /** Validate function for search inputs. */
    const isInputValid = () => {
        return validateFields(
            {
                quotationDate: () => {
                    const [startDate, endDate] = searchData.quotationDate;

                    // Valid if both are valid dates or both are null.
                    const bothValid = startDate?.isValid?.() && endDate?.isValid?.();
                    const bothNull = startDate === null && endDate === null;

                    return bothValid || bothNull;
                },
            },
            setSearchValidator
        );
    };

    /** Handle searching for filtered quotations by calling the GET API. */
    const handleSearch = () => {
        if (!isInputValid()) {
            return dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please complete the required input fields before proceeding.',
                    severity: 'warning',
                })
            );
        }

        // Apply `JSON.stringify()` to the state to ensure explicit stringification, preventing unintended automatic serialization.
        // Search results must match the days the user picked. Leaving the dayjs
        // range to JSON.stringify converts it to UTC, which drops or adds a
        // boundary day for users working from Korea.
        const searchValue = JSON.stringify({
            il_trade_partner: searchData.tradePartner || null,
            start: searchData.quotationDate?.[0] ? searchData.quotationDate[0].format('YYYY-MM-DD') : null,
            end: searchData.quotationDate?.[1] ? searchData.quotationDate[1].format('YYYY-MM-DD') : null,
        });

        callAPI({
            method: 'post',
            module: 'quote_archive',
            component: 'search',
            getAccessTokenSilently,
            email: user.email,
            obj: searchValue,
            onInit: () => setInitialLoading(false),
            onSuccess: (resp) => {
                setRowCount(resp.data.total);
                setQuoteRows(resp.data.il_quotation);
            },
            onError: () => {
                dispatch(setSnackbar({ open: true, message: 'Something wrong, please try again.', severity: 'error' }));
            },
            onFinal: () => setInitialLoading(true),
        });
    };

    /** Handler for downloading quote by calling GET API.
     *  Response of GET API is expected to be an encoded file.
     *  DownloadBlobAsZip function is used to preprocess and handle the downloaded file.
     */
    const handleDownload = (row) => {
        callAPI({
            method: 'get',
            module: 'quote_archive',
            component: row.id,
            type: 'blob',
            getAccessTokenSilently,
            email: user.email,
            responseType: 'blob',
            onInit: () => setActionLoading(true),
            onSuccess: (resp) => DownloadBlobAsZip(row.title, resp.data),
            onError: () => {
                dispatch(setSnackbar({ open: true, message: 'Something wrong, please try again.', severity: 'error' }));
            },
            onFinal: () => setActionLoading(false),
        });
    };

    /** Handler for deleting quote by calling DELETE API and trigger GET API request.
     * But permission is restricted based on userRole.
     * Assistant Manager and above (Operation-Manager, Administrator) can delete.
     */
    const handleDelete = (deletedId) => {
        if (isAssistantManagerOrAbove(userRole)) {
            if (!window.confirm('Once deleted, the file cannot be recovered. Do you want to proceed?')) return;
        } else {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'You do not have permission to delete the quote.',
                    severity: 'warning',
                })
            );
            return;
        }

        callAPI({
            method: 'delete',
            module: 'quote_archive',
            getAccessTokenSilently,
            email: user.email,
            obj: deletedId,
            onInit: () => setActionLoading(true),
            onSuccess: (resp) => {
                setRowUpdateTrigger((prev) => !prev);
                dispatch(setSnackbar({ open: true, message: 'Successfully deleted.', severity: 'success' }));
            },
            onError: () => {
                dispatch(setSnackbar({ open: true, message: 'Something wrong, please try again.', severity: 'error' }));
            },
            onFinal: () => setActionLoading(false),
        });
    };

    return (
        <>
            <CircularLoading loading={actionLoading} />
            <Paper sx={customized_style.itemPaper}>
                <PageHeader
                    title={'QUOTE ARCHIVE'}
                    icon={ArchiveIcon}
                    children={
                        <Box sx={{ ml: 'auto' }}>
                            <ThemeButton
                                role="New"
                                text="New"
                                icon={<AddIcon />}
                                onClick={handleOpenDialog}
                                sx={{ px: '7px' }}
                            />
                        </Box>
                    }
                    sx={{ mb: 0 }}
                />
                <Box sx={{ display: 'flex', gap: '35px' }}>
                    <Box className="search-field" sx={{ width: '25vw', mt: 5 }}>
                        <AutocompleteFieldTradePartner
                            key="Trade Partner"
                            label="Trade Partner"
                            name="tradePartner"
                            value={searchData.tradePartner}
                            onChange={(event, newValue) => handleSearchFormChange('tradePartner', newValue)}
                            sx={{ display: 'flex', justifyContent: 'space-between' }}
                            options={tradePartnerList}
                            AutocompleteStyle={{ width: '80%' }}
                            disable
                        />
                        <Box sx={{ display: 'flex', mt: '22px', gap: '25px', justifyContent: 'space-between' }}>
                            <InputLabel
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#003a52',
                                    textWrap: 'pretty',
                                    width: '100px',
                                }}
                            >
                                Quotation Date
                            </InputLabel>
                            <Box sx={{ width: '80%' }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateRangePicker
                                        value={searchData.quotationDate}
                                        onChange={(newValue) => handleSearchFormChange('quotationDate', newValue)}
                                        slots={operationRangeDaySlot}
                                        slotProps={{
                                            textField: {
                                                error: searchValidator.quotationDate,
                                                InputLabelProps: { shrink: true },
                                                'data-testid': 'date-range-input',
                                            },
                                        }}
                                        sx={{
                                            '& .MuiInputBase-input': { px: 2, py: 0, height: '27px' },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 2,
                                mt: '22px',
                            }}
                        >
                            <Button variant="contained" onClick={handleSearch} sx={{ width: '80%' }}>
                                SEARCH
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => setSearchData({ tradePartner: null, quotationDate: [null, null] })}
                                sx={{ width: '80%' }}
                            >
                                RESET
                            </Button>
                        </Box>
                    </Box>
                    <MainDatagrid
                        rows={quoteRows}
                        cols={getColumns(handleDownload, handleDelete)}
                        id_status={false}
                        showToolbar="CustomToolbar"
                        additional={{
                            loading: !initialLoading,
                            rowCount: rowCount,
                            pageSizeOptions: [25, 50, 100],
                            paginationModel: paginationModel,
                            paginationMode: 'server',
                            onPaginationModelChange: setPaginationModel,
                            disableColumnMenu: true,
                        }}
                        sx={{ height: '85vh', fontSize: 12 }}
                    />
                </Box>
            </Paper>
            <QuoteDialog
                dispatch={dispatch}
                newOpen={newOpen}
                setNewOpen={setNewOpen}
                tradePartnerList={tradePartnerList}
                setRowUpdateTrigger={setRowUpdateTrigger}
                setActionLoading={setActionLoading}
                setSearchData={setSearchData}
            />
            <CustomSnackbar />
        </>
    );
}
